import {
  BooleanOrNullOrUndefined,
  DateOrNullOrUndefined,
  ForceMap,
  NumberOrNullOrUndefined,
  StringOrNullOrUndefined,
} from 'src/app/shared/utils';
import { PaServiceV2 } from './pa-service.v2.model';
import { Parent } from './parents.v2.model';
import { LocationV2 } from "./location.v2.model";
import { ClientCommunications } from './client-communications.model';
import {
  InsuranceProduct,
  INSURANCE_PRODUCTS,
} from 'src/app/pages/medical/insurance/insurance.interface';
import { formatDateToYmd } from 'src/app/shared/utils/formatDate';
import { parseYMDDate } from 'src/app/shared/utils/DateOrNullOrUndefined';

export interface DoctorEmbed {
  id: number;
  name: string;
  surname: string | null;
}

export interface PatientDoctors {
  rbt_home: DoctorEmbed | null;
  rbt2_school: DoctorEmbed | null;
  bcba_home: DoctorEmbed | null;
  bcba2_school: DoctorEmbed | null;
  clin_director: DoctorEmbed | null;
  case_manager: DoctorEmbed | null;
}

export interface InsurerEmbed {
  id: number;
  name: string | null;
}

export class PatientV2 {
  id!: number;
  first_name!: string;
  last_name!: string;
  full_name!: string;
  parents_id?: number;
  parent?: Parent;
  // patient_id: number;
  patient_identifier!: string;
  client_consent_id?: string;
  avatar!: string;
  status!: string;

  insurer_id!: number;
  insurer_secondary_id!: number;
  insurance_identifier!: string;
  insurance_secondary_identifier!: string;
  insurance_status!: string;
  insurance_secondary_status!: string;
  insurance_product!: string;
  birth_date!: Date;

  age!: number;
  gender!: number;
  parent_gender!: number;
  education!: string;
  profession!: string;
  school_name!: string;
  school_number!: string;

  parent_guardian_name!: string;
  relationship!: string;
  parent_birth_date!: Date;
  emmployment!: boolean;
  auto_accident!: boolean;
  other_accident!: boolean;
  is_self_subscriber!: boolean;

  language!: string;
  phone!: string;
  home_phone!: string;
  work_phone!: string;
  email!: string;
  city!: string;

  zip!: string;
  state!: string;
  address!: string;
  parent_address!: string;
  parent_city!: string;
  parent_state!: string;
  parent_zip!: string;
  special_note!: string;
  link_documentation?: string;
  diagnosis_code!: string;
  other_diagnosis_code?: string;
  diagnosis_level!: string;
  secondary_diagnosis_codes!: string;
  other_secundary_diagnosis_code?: string;
  insured_policy_group!: string;
  referring_provider_first_name!: string;
  referring_provider_last_name!: string;
  referring_provider_npi!: string;
  referring_code!: string;
  npi!: string;
  schedule!: string;
  summer_schedule!: string;
  location_id!: number;

  eqhlid!: string;
  elegibility_date!: Date;
  deductible_individual_I_F!: string;
  balance!: string;
  coinsurance!: string;
  copayments!: string;
  oop!: string;
  patient_control!: string;

  // Secondary Insurance
  secondary_elegibility_date!: Date;
  secondary_deductible_individual_I_F!: string;
  secondary_balance!: string;
  secondary_coinsurance!: string;
  secondary_copayments!: string;
  secondary_oop!: string;
  secondary_eqhlid!: string;
  secondary_telehealth!: boolean;
  secondary_pay!: boolean;
  secondary_is_self_subscriber!: boolean;
  secondary_insurance_product!: string;

  // 'secondary_elegibility_date' => 'nullable|date',
  //           'secondary_pos_covered' => 'nullable|array',
  //           'secondary_deductible_individual_I_F' => 'nullable|string|max:150',
  //           'secondary_balance' => 'nullable|string|max:150',
  //           'secondary_coinsurance' => 'nullable|string|max:150',
  //           'secondary_copayments' => 'nullable|string|max:150',
  //           'secondary_oop' => 'nullable|string|max:150',
  //           'secondary_eqhlid' => 'nullable|string',
  //           'secondary_telehealth' => 'nullable|boolean',
  //           'secondary_pay' => 'nullable|boolean',
  //           'secondary_is_self_subscriber' => 'nullable|boolean',

  welcome!: string;
  consent!: string;
  insurance_card!: string;
  eligibility!: string;
  mnl!: string;
  referral!: string;
  ados!: string;
  iep!: string;
  asd_diagnosis!: string;
  is_dsm5_diagnostic!: string;
  cde!: string;
  submitted!: string;
  interview!: string;
  //
  rbt_home_id!: number;
  rbt2_school_id!: number;
  bcba_home_id!: number;
  bcba_school_id!: number;
  bcba2_school_id!: number;
  clin_director_id!: number;
  case_manager_id!: number;
  doctors?: PatientDoctors;
  insurer?: InsurerEmbed | null;
  insurance_secondary?: InsurerEmbed | null;
  telehealth!: boolean;
  pay!: boolean;

  //
  pa_services!: PaServiceV2[];
  pa_assessments!: string;
  compayment_per_visit!: string;
  insurer_secundary!: string;
  communication?: ClientCommunications;
  //
  created_at?: Date;
  updated_at?: Date;
  deleted_at?: Date;

  location?: LocationV2;
  plan_payment?: boolean;
  plan_amount?: number;
  plan_number_of_payments?: number;
  plan_frequency?: string;

  constructor(data: Partial<PatientV2> = {}) {
    const result = {
      // ...data,
      id: NumberOrNullOrUndefined(data.id),
      first_name: StringOrNullOrUndefined(data.first_name),
      last_name: StringOrNullOrUndefined(data.last_name),
      full_name: `${data.first_name} ${data.last_name}`,
      avatar: StringOrNullOrUndefined(data.avatar),
      status: StringOrNullOrUndefined(data.status),

      parents_id: NumberOrNullOrUndefined(data.parents_id),

      parent: data.parent ?? null,

      birth_date: parsePatientDateOnlyValue(data.birth_date),

      // patient_id: NumberOrNullOrUndefined(data.patient_id),
      patient_identifier: StringOrNullOrUndefined(data.patient_identifier),
      insurer_id: NumberOrNullOrUndefined(data.insurer_id),
      insurer_secondary_id: NumberOrNullOrUndefined(data.insurer_secondary_id),
      insurance_identifier: StringOrNullOrUndefined(data.insurance_identifier),
      insurance_secondary_identifier: StringOrNullOrUndefined(
        data.insurance_secondary_identifier
      ),
      insurance_status: StringOrNullOrUndefined(data.insurance_status),
      insurance_secondary_status: StringOrNullOrUndefined(data.insurance_secondary_status),
      insurance_product: INSURANCE_PRODUCTS.includes(
        data?.insurance_product as InsuranceProduct,
      )
        ? (data.insurance_product as InsuranceProduct)
        : null,
      language: StringOrNullOrUndefined(data.language),
      city: StringOrNullOrUndefined(data.city),
      email: StringOrNullOrUndefined(data.email),
      phone: StringOrNullOrUndefined(data.phone),
      home_phone: StringOrNullOrUndefined(data.home_phone),
      work_phone: StringOrNullOrUndefined(data.work_phone),
      age: NumberOrNullOrUndefined(data.age),
      gender: NumberOrNullOrUndefined(data.gender),
      parent_gender: NumberOrNullOrUndefined(data.parent_gender),
      education: StringOrNullOrUndefined(data.education),

      profession: StringOrNullOrUndefined(data.profession),
      school_name: StringOrNullOrUndefined(data.school_name),
      school_number: StringOrNullOrUndefined(data.school_number),
      parent_guardian_name: StringOrNullOrUndefined(data.parent_guardian_name),
      relationship: StringOrNullOrUndefined(data.relationship),
      parent_birth_date: parsePatientDateOnlyValue(data.parent_birth_date),
      emmployment: BooleanOrNullOrUndefined(data.emmployment),
      auto_accident: BooleanOrNullOrUndefined(data.auto_accident),
      other_accident: BooleanOrNullOrUndefined(data.other_accident),
      is_self_subscriber: BooleanOrNullOrUndefined(data.is_self_subscriber),

      zip: StringOrNullOrUndefined(data.zip),
      state: StringOrNullOrUndefined(data.state),
      address: StringOrNullOrUndefined(data.address),
      parent_address: StringOrNullOrUndefined(data.parent_address),
      parent_city: StringOrNullOrUndefined(data.parent_city),
      parent_state: StringOrNullOrUndefined(data.parent_state),
      parent_zip: StringOrNullOrUndefined(data.parent_zip),
      special_note: StringOrNullOrUndefined(data.special_note),
      link_documentation: StringOrNullOrUndefined(data.link_documentation),
      diagnosis_code: StringOrNullOrUndefined(data.diagnosis_code),
      other_diagnosis_code: StringOrNullOrUndefined(data.other_diagnosis_code),
      diagnosis_level: StringOrNullOrUndefined(data.diagnosis_level),
      secondary_diagnosis_codes: StringOrNullOrUndefined(data.secondary_diagnosis_codes),
      other_secundary_diagnosis_code: StringOrNullOrUndefined(data.other_secundary_diagnosis_code),
      insured_policy_group: StringOrNullOrUndefined(data.insured_policy_group),
      referring_provider_first_name: StringOrNullOrUndefined(
        data.referring_provider_first_name
      ),
      referring_provider_last_name: StringOrNullOrUndefined(
        data.referring_provider_last_name
      ),
      referring_provider_npi: StringOrNullOrUndefined(
        data.referring_provider_npi
      ),
      referring_code: StringOrNullOrUndefined(data.referring_code),
      npi: StringOrNullOrUndefined(data.npi),
      schedule: StringOrNullOrUndefined(data.schedule),
      summer_schedule: StringOrNullOrUndefined(data.summer_schedule),
      location_id: NumberOrNullOrUndefined(data.location_id),

      eqhlid: StringOrNullOrUndefined(data.eqhlid),
      elegibility_date: parsePatientDateOnlyValue(data.elegibility_date),
      deductible_individual_I_F: StringOrNullOrUndefined(
        data.deductible_individual_I_F
      ),
      balance: StringOrNullOrUndefined(data.balance),
      coinsurance: StringOrNullOrUndefined(data.coinsurance),
      copayments: StringOrNullOrUndefined(data.copayments),
      oop: StringOrNullOrUndefined(data.oop),
      patient_control: StringOrNullOrUndefined(data.patient_control),

      // Secondary Insurance
      secondary_elegibility_date: parsePatientDateOnlyValue(data.secondary_elegibility_date),
      secondary_deductible_individual_I_F: StringOrNullOrUndefined(data.secondary_deductible_individual_I_F),
      secondary_balance: StringOrNullOrUndefined(data.secondary_balance),
      secondary_coinsurance: StringOrNullOrUndefined(data.secondary_coinsurance),
      secondary_copayments: StringOrNullOrUndefined(data.secondary_copayments),
      secondary_oop: StringOrNullOrUndefined(data.secondary_oop),
      secondary_eqhlid: StringOrNullOrUndefined(data.secondary_eqhlid),
      secondary_telehealth: BooleanOrNullOrUndefined(data.secondary_telehealth),
      secondary_pay: BooleanOrNullOrUndefined(data.secondary_pay),
      secondary_is_self_subscriber: BooleanOrNullOrUndefined(data.secondary_is_self_subscriber),
      secondary_insurance_product: INSURANCE_PRODUCTS.includes(
        data?.secondary_insurance_product as InsuranceProduct,
      )
        ? (data.secondary_insurance_product as InsuranceProduct)
        : null,

      welcome: StringOrNullOrUndefined(data.welcome),
      consent: StringOrNullOrUndefined(data.consent),
      insurance_card: StringOrNullOrUndefined(data.insurance_card),
      eligibility: StringOrNullOrUndefined(data.eligibility),
      mnl: StringOrNullOrUndefined(data.mnl),
      referral: StringOrNullOrUndefined(data.referral),
      ados: StringOrNullOrUndefined(data.ados),
      iep: StringOrNullOrUndefined(data.iep),
      asd_diagnosis: StringOrNullOrUndefined(data.asd_diagnosis),
      is_dsm5_diagnostic: StringOrNullOrUndefined(data.is_dsm5_diagnostic),
      cde: StringOrNullOrUndefined(data.cde),
      submitted: StringOrNullOrUndefined(data.submitted),
      interview: StringOrNullOrUndefined(data.interview),

      pa_services: ForceMap(data.pa_services ?? [], PaServiceV2),
      pa_assessments: StringOrNullOrUndefined(data.pa_assessments),
      compayment_per_visit: StringOrNullOrUndefined(data.compayment_per_visit),
      insurer_secundary: StringOrNullOrUndefined(data.insurer_secundary),

      rbt_home_id: NumberOrNullOrUndefined(data.rbt_home_id),
      rbt2_school_id: NumberOrNullOrUndefined(data.rbt2_school_id),
      bcba_home_id: NumberOrNullOrUndefined(data.bcba_home_id),
      bcba_school_id: NumberOrNullOrUndefined(data.bcba_school_id),
      bcba2_school_id: NumberOrNullOrUndefined(data.bcba2_school_id),
      clin_director_id: NumberOrNullOrUndefined(data.clin_director_id),
      case_manager_id: NumberOrNullOrUndefined(data.case_manager_id),
      doctors: data.doctors ?? undefined,
      insurer: data.insurer ?? null,
      insurance_secondary: data.insurance_secondary ?? null,
      telehealth: BooleanOrNullOrUndefined(data.telehealth),
      pay: BooleanOrNullOrUndefined(data.pay),

      created_at: DateOrNullOrUndefined(data.created_at),
      updated_at: DateOrNullOrUndefined(data.updated_at),
      deleted_at: DateOrNullOrUndefined(data.deleted_at),
      location: data.location ?? null,
      communication: data.communication ?? null,
      plan_payment: BooleanOrNullOrUndefined(data.plan_payment),
      plan_amount: NumberOrNullOrUndefined(data.plan_amount),
      plan_number_of_payments: NumberOrNullOrUndefined(data.plan_number_of_payments),
      plan_frequency: StringOrNullOrUndefined(data.plan_frequency),
    };

    Object.assign(this, result);
  }
}

function parsePatientDateOnlyValue(value: Date | string | null | undefined): Date | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  const normalized = formatDateToYmd(value);
  if (normalized) {
    return parseYMDDate(normalized);
  }

  return DateOrNullOrUndefined(value);
}
